import { useRef, useState, type ChangeEvent } from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    MESSAGE_ATTACHMENT_ACCEPT,
    MESSAGE_ATTACHMENT_MAX_BYTES,
    MESSAGE_ATTACHMENT_MAX_FILES,
    formatMessageFileSize,
} from "./messageAttachment.utils";

export default function MessageComposer({
    disabled = false,
    sending = false,
    placeholder = "Напиши съобщение...",
    onSend,
}: {
    disabled?: boolean;
    sending?: boolean;
    placeholder?: string;
    onSend: (body: string, files: File[]) => void | Promise<void>;
}) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [draft, setDraft] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);

    function resetComposer() {
        setDraft("");
        setSelectedFiles([]);
        setFileError(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }

    function handlePickFiles(event: ChangeEvent<HTMLInputElement>) {
        const incoming = Array.from(event.target.files ?? []);

        if (incoming.length === 0) {
            return;
        }

        const nextFiles = [...selectedFiles];
        let error: string | null = null;

        for (const file of incoming) {
            if (nextFiles.length >= MESSAGE_ATTACHMENT_MAX_FILES) {
                error = `Можете да прикачите до ${MESSAGE_ATTACHMENT_MAX_FILES} файла.`;
                break;
            }

            if (file.size > MESSAGE_ATTACHMENT_MAX_BYTES) {
                error = `Файлът „${file.name}“ надхвърля 10 MB.`;
                continue;
            }

            nextFiles.push(file);
        }

        setSelectedFiles(nextFiles);
        setFileError(error);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }

    function removeFile(index: number) {
        setSelectedFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
        setFileError(null);
    }

    async function handleSend() {
        const body = draft.trim();

        if ((!body && selectedFiles.length === 0) || disabled || sending) {
            return;
        }

        try {
            await onSend(body, selectedFiles);
            resetComposer();
        } catch {
            // Грешката се показва от родителския mutation.
        }
    }

    const canSend = (draft.trim().length > 0 || selectedFiles.length > 0) && !disabled && !sending;

    return (
        <Stack spacing={1}>
            {fileError ? <Alert severity="warning">{fileError}</Alert> : null}

            {selectedFiles.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {selectedFiles.map((file, index) => (
                        <Chip
                            key={`${file.name}-${file.size}-${index}`}
                            size="small"
                            label={`${file.name} (${formatMessageFileSize(file.size)})`}
                            onDelete={() => removeFile(index)}
                            deleteIcon={<CloseIcon />}
                            sx={{ maxWidth: "100%" }}
                        />
                    ))}
                </Box>
            ) : null}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { sm: "flex-end" } }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept={MESSAGE_ATTACHMENT_ACCEPT}
                    onChange={handlePickFiles}
                />

                <Tooltip title="Прикачи файл (до 10 MB)">
                    <span>
                        <IconButton
                            aria-label="Прикачи файл"
                            disabled={disabled || sending || selectedFiles.length >= MESSAGE_ATTACHMENT_MAX_FILES}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ alignSelf: { xs: "flex-start", sm: "flex-end" } }}
                        >
                            <AttachFileIcon />
                        </IconButton>
                    </span>
                </Tooltip>

                <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    placeholder={placeholder}
                    value={draft}
                    disabled={disabled || sending}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            handleSend();
                        }
                    }}
                />

                <Button
                    variant="contained"
                    disabled={!canSend}
                    sx={{ alignSelf: { xs: "stretch", sm: "flex-end" }, minWidth: { sm: 110 } }}
                    onClick={handleSend}
                >
                    {sending ? "Изпращане..." : "Изпрати"}
                </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary">
                PDF, Office, изображения, TXT, CSV или архив — до {MESSAGE_ATTACHMENT_MAX_FILES} файла по 10 MB.
            </Typography>
        </Stack>
    );
}
